import React, { useMemo } from 'react';
import { Row, Col, FormGroup, Label, Input, Card, CardBody } from 'reactstrap';
import Select from 'components/Select';
import StatesInput from 'components/StatesInput';
import { useUpdateSalesLocation } from '../../api/sales-locations/service';
import { useGetCountries } from '../../api/countries/service';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from 'utils/sidebarShellTheme';

const TaxInfo = ({ formData, activeColor, isTheme2 = false, salesLocationId, onSave, salesTaxes = [] }) => {
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const { data: countriesData } = useGetCountries();
    const usCountryId = useMemo(() => {
        const countries = countriesData?.data || [];
        const usCountry = countries.find(
            (country) =>
                country.name === 'United States' ||
                country.abbreviation === 'US' ||
                country.code === 'US'
        );
        return usCountry ? String(usCountry.id) : '';
    }, [countriesData]);
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    const inputStyle = useMemo(
        () => (isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined),
        [isTheme2]
    );
    // Safety check - if formData is undefined, provide default values
    const safeFormData = formData || {};
    const updateSalesLocationMutation = useUpdateSalesLocation();

    const taxNumberOptions = React.useMemo(() => {
        let options = [{ value: '', label: 'Select Tax Number...' }];
        if (Array.isArray(salesTaxes)) {
            salesTaxes.forEach(tax => {
                const label = tax.tax_number || tax.name || tax.documentId || `Tax ${tax.id}`;
                const value = tax.tax_number || tax.id || tax.documentId;
                if (value) {
                    options.push({ value: String(value), label: String(label) });
                }
            });
        }
        options.push({ value: 'ADD_NEW', label: '[Add New]' });
        return options;
    }, [salesTaxes]);

    // Initialize local form data correctly handling tax_number for dropdown selection
    const initializeFormData = React.useCallback((data) => {
        if (!data) return {};

        const taxNumStr = data.tax_number ? String(data.tax_number) : '';
        const existsInOptions = Array.isArray(salesTaxes) && salesTaxes.some(t => String(t.tax_number || t.id || t.documentId) === taxNumStr);

        return {
            ...data,
            selected_tax_option: taxNumStr ? (existsInOptions ? taxNumStr : 'ADD_NEW') : ''
        };
    }, [salesTaxes]);

    // Local state for form data
    const [localFormData, setLocalFormData] = React.useState(() => initializeFormData(formData));

    // Update local state when formData or salesTaxes prop changes
    React.useEffect(() => {
        setLocalFormData(initializeFormData(formData));
    }, [formData, initializeFormData]);

    const saveTaxInfoRef = React.useRef();

    // Function to save tax info data
    const saveTaxInfo = React.useCallback(() => {
        if (salesLocationId && localFormData) {
            const salesTaxFields = [
                'tax_payer_name',
                'address_1', 'address_2', 'zip', 'city',
                'county', 'country', 'state', 'phone', 'fax'
            ];

            const isAddNew = localFormData.selected_tax_option === 'ADD_NEW';

            let selectedDocumentId = null;
            if (!isAddNew) {
                const selectedTax = Array.isArray(salesTaxes) && salesTaxes.find(t => String(t.tax_number || t.id || t.documentId) === localFormData.selected_tax_option);
                if (selectedTax) {
                    selectedDocumentId = selectedTax.documentId || selectedTax.id;
                }
            }

            const sales_tax_info = {
                is_new: isAddNew,
                tax_number: isAddNew ? localFormData.tax_number : localFormData.selected_tax_option,
                ...(selectedDocumentId && { documentId: selectedDocumentId })
            };
            const tax_info = {};

            Object.keys(localFormData).forEach(key => {
                // Exclude the internal dropdown state and typing state from looping map
                if (key === 'selected_tax_option' || key === 'tax_number') return;

                if (salesTaxFields.includes(key)) {
                    sales_tax_info[key] = localFormData[key];
                } else {
                    tax_info[key] = localFormData[key];
                }
            });

            if (usCountryId) {
                sales_tax_info.country = usCountryId;
                tax_info.collector_country = usCountryId;
                tax_info.appraisal_country = usCountryId;
            }

            const updateData = {
                sales_tax_info,
                tax_info
            };

            updateSalesLocationMutation.mutate({
                id: salesLocationId,
                data: updateData
            });
        }
    }, [salesLocationId, localFormData, updateSalesLocationMutation, usCountryId]);

    // Update ref whenever saveTaxInfo changes
    React.useEffect(() => {
        saveTaxInfoRef.current = saveTaxInfo;
    }, [saveTaxInfo]);

    // Call onSave callback when component mounts to register save function
    React.useEffect(() => {
        if (onSave) {
            onSave('TAX_INFO', () => saveTaxInfoRef.current?.());
        }
    }, [onSave]);

    // Disable condition: true if no option is chosen from the select dropdown
    const isSalesTaxFormsDisabled = !localFormData.selected_tax_option;
    return (
        <div className="">
            <Row>
                <Col md="12">
                    <Card>
                        <CardBody>
                            <h5 className="mb-3">SALES TAX INFORMATION</h5>
                            <Row>
                                <Col md={localFormData.selected_tax_option === 'ADD_NEW' ? 6 : 12}>
                                    <FormGroup>
                                        <Label className='form-control-label'>Sales Tax Number:</Label>
                                        <Select
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            name="sales_tax_number_select"
                                            options={taxNumberOptions}
                                            value={taxNumberOptions.find(opt => opt.value === localFormData.selected_tax_option) || null}
                                            onChange={(option) => {
                                                const value = option ? option.value : '';
                                                let newData = { selected_tax_option: value, tax_number: '' };

                                                const salesTaxFields = [
                                                    'tax_payer_name', 'address_1', 'address_2', 'zip', 'city',
                                                    'county', 'country', 'state', 'phone', 'fax'
                                                ];

                                                if (value === 'ADD_NEW' || !value) {
                                                    // Clear fields for fresh inputs
                                                    salesTaxFields.forEach(field => {
                                                        newData[field] = '';
                                                    });
                                                    // State object also needs to be cleared exactly
                                                    newData['state'] = null;
                                                } else {
                                                    // Auto-populate inputs from matching salesTaxes array object
                                                    const selectedTax = Array.isArray(salesTaxes) && salesTaxes.find(t => String(t.tax_number || t.id || t.documentId) === value);
                                                    if (selectedTax) {
                                                        salesTaxFields.forEach(field => {
                                                            newData[field] = selectedTax[field] || '';
                                                        });
                                                        // Ensure custom object handles states safely
                                                        newData['state'] = selectedTax.state || null;
                                                    }
                                                }

                                                setLocalFormData(prev => ({ ...prev, ...newData }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                {localFormData.selected_tax_option === 'ADD_NEW' && (
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>New Tax Number:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.tax_number || ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, tax_number: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                )}
                            </Row>
                            <Row>
                                <Col md="12">
                                    <FormGroup>
                                        <Label className='form-control-label'>Tax Payer Name:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.tax_payer_name}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, tax_payer_name: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>Address 1:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.address_1}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, address_1: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>Address 2:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.address_2}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, address_2: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>ZIP:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.zip}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, zip: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>City:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.city}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, city: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>County:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.county}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, county: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>State:</Label>
                                        <StatesInput
                                            name="state"
                                            activeColor={activeColor}
                                            styles={selectStyles}
                                            className="react-select"
                                            isDisabled={isSalesTaxFormsDisabled}
                                            value={localFormData.state}
                                            onChange={(stateObject) => {
                                                const value = stateObject ? {
                                                    id: stateObject.id,
                                                    abbreviation: stateObject.abbreviation
                                                } : null;
                                                setLocalFormData(prev => ({ ...prev, state: value }));
                                            }}
                                            placeholder="Select State"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>Phone 1:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.phone}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, phone: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label className='form-control-label'>Fax 1:</Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            style={inputStyle}
                                            disabled={isSalesTaxFormsDisabled}
                                            value={localFormData.fax}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setLocalFormData(prev => ({ ...prev, fax: value }));
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {localFormData.selected_tax_option !== 'ADD_NEW' && (
                <Row>
                    <Col md="6">
                        <Card>
                            <CardBody>
                                <h5 className="mb-3">COLLECTOR INFORMATION</h5>
                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className='form-control-label'>Collector Name:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.collector_name}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, collector_name: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>Address 1:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.collector_address_1}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, collector_address_1: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>Address 2:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.collector_address_2}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, collector_address_2: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>ZIP:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.collector_zip}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, collector_zip: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>City:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.collector_city}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, collector_city: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>County:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.collector_county}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, collector_county: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>State:</Label>
                                            <StatesInput
                                                name="collector_state"
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                value={localFormData.collector_state}
                                                onChange={(stateObject) => {
                                                    const value = stateObject ? {
                                                        id: stateObject.id,
                                                        abbreviation: stateObject.abbreviation
                                                    } : null;
                                                    setLocalFormData(prev => ({ ...prev, collector_state: value }));
                                                }}
                                                placeholder="Select State"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>Phone 1:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.collector_phone_1}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, collector_phone_1: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md="6">
                        <Card>
                            <CardBody>
                                <h5 className="mb-3">VIT / INVENTORY TAX INFORMATION</h5>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>Account #:</Label>
                                            <Select
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                value={localFormData.vit_account_number ? { value: localFormData.vit_account_number, label: localFormData.vit_account_number } : null}
                                                onChange={(option) => {
                                                    const value = option?.value || '';
                                                    setLocalFormData(prev => ({ ...prev, vit_account_number: value }));
                                                }}
                                                options={[
                                                    { value: 'P147537', label: 'P147537' }
                                                ]}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>New Account #:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.vit_new_account_number}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, vit_new_account_number: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {/* APPRAISAL DISTRICT INFORMATION */}
                                <h5 className="mb-3 mt-4">APPRAISAL DISTRICT INFORMATION</h5>
                                <Row>
                                    <Col md="12">
                                        <FormGroup>
                                            <Label className='form-control-label'>District Name:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.appraisal_district_name}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_district_name: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>Address 1:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.appraisal_address_1}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_address_1: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>Address 2:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.appraisal_address_2}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_address_2: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>ZIP:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.appraisal_zip}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_zip: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>City:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.appraisal_city}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_city: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>County:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.appraisal_county}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_county: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>State:</Label>
                                            <StatesInput
                                                name="appraisal_state"
                                                activeColor={activeColor}
                                                styles={selectStyles}
                                                className="react-select"
                                                value={localFormData.appraisal_state}
                                                onChange={(stateObject) => {
                                                    const value = stateObject ? {
                                                        id: stateObject.id,
                                                        abbreviation: stateObject.abbreviation
                                                    } : null;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_state: value }));
                                                }}
                                                placeholder="Select State"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label className='form-control-label'>Phone 1:</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                style={inputStyle}
                                                value={localFormData.appraisal_phone_1}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setLocalFormData(prev => ({ ...prev, appraisal_phone_1: value }));
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    </Col>

                </Row>
            )}
        </div>
    );
};

export default TaxInfo;

